/**
 * Adventure Tourism's own site list - a dedicated dataset, not a filtered
 * view of the generic Sight/place catalog used by the rest of the site's
 * map. Each entry is a specific place tied to a specific adventure activity,
 * with a blurb about what actually happens there (not a generic tourism
 * blurb about the place itself). Coordinates are either the same real,
 * already-vetted values used elsewhere in the codebase's place catalog
 * (K2, Concordia, Askole, etc.), or, for places with no existing entry
 * (Khanpur Dam, Chapursan Valley, Bahawalpur, Shispare Sar), sourced
 * directly and verified against public references - never guessed.
 *
 * `type` matches the label already used for this content elsewhere on the
 * /experiences/adventure page (GUIDE_ITEM_BADGES in page.tsx) so the same
 * name means the same thing in both places.
 */

export const DIFFICULTY_LEVELS = ["Beginner", "Intermediate", "Advanced", "Expert"] as const;
export type Difficulty = (typeof DIFFICULTY_LEVELS)[number];

export type AdventureSite = {
  slug: string;
  name: string;
  type: string;
  color: string;
  /**
   * The same hue as `color`, but fixed for surfaces that are dark in BOTH
   * themes (the map's detail panel, the bento tiles). `color` is tuned for the
   * light basemap tiles and may be a theme token, which flips: var(--dot)
   * resolves to #464feb in light theme and vanishes on a near-black panel.
   * Supplied by AdventureType.onDarkColor; optional only because the static
   * fallback rows below predate the column.
   */
  onDarkColor?: string;
  region: string;
  lat: number;
  lng: number;
  /** CMS override for the map card's Google Maps link; empty derives it from
   *  lat/lng. See lib/google-maps-link.ts. */
  googleMapsUrl?: string | null;
  /** What specifically happens at this place - not a generic place blurb. */
  blurb: string;
  /**
   * Only set where the site's own existing editorial content (place-guides.ts)
   * states or clearly implies a difficulty - e.g. "no crampons needed" or
   * "not for inexperienced trekkers". Left undefined everywhere else rather
   * than guessed - a wrong difficulty rating on a real trek isn't cosmetic,
   * it's the kind of thing someone could plan a trip around.
   */
  difficulty?: Difficulty;
  /**
   * Only set where a real elevation figure already appears in this site's
   * own existing content (a pass/lake/summit height stated in the blurb or
   * source text) - never estimated from a map. Left undefined elsewhere.
   */
  elevationM?: number;
};

const TYPE_COLORS: Record<string, string> = {
  "High Trekking": "var(--cta-bright)",
  "Glacier Walk": "var(--dot)",
  "Extreme White-water": "#ef4444",
  "High Adrenaline": "#f97316",
  "Elite Climbing": "#ef4444",
  "Extreme 4x4": "#f97316",
  "Traditional Sport": "var(--dot)",
  "High Altitude": "var(--dot)",
};

function site(input: Omit<AdventureSite, "color">): AdventureSite {
  return { ...input, color: TYPE_COLORS[input.type] };
}

export type AdventureTypeMeta = {
  type: string;
  color: string;
  /** See AdventureSite.onDarkColor — same reason, for the bento tile labels. */
  onDarkColor?: string;
  /** Same photo already used for this activity on the guide section above - reused, not new. */
  image: string;
  /** What the activity itself is - never a place name, that's what the map is for. */
  description: string;
  /** Adventure page tile heading, short line and photo credit (CMS); "" = unset. */
  title?: string;
  tagline?: string;
  imageCredit?: string;
  count: number;
};

const TYPE_IMAGES: Record<string, string> = {
  "High Trekking": "/images/licensed/baltoro-glacier-from-air.jpg",
  // Real glacier ice/snout, not the green valley floor below it.
  "Glacier Walk": "/images/attractions/snow-lake.jpg",
  // Actual white-water on the Swat River, not a generic forest-valley shot.
  "Extreme White-water": "/images/attractions/swat-white-water.jpg",
  // A real paragliding launch, not a static ski-town rooftop view.
  "High Adrenaline": "/images/Khyber Pakhtunkhwa Pakistan A vibrant paragliding day at a lake in Khyber Pakhtunkhwa showcasing colorful parachutes and scenic views.jpg",
  // The Trango Towers themselves, not a group of hikers.
  "Elite Climbing": "/images/attractions/trango-towers.jpg",
  // An actual jeep rally in the dunes, not a mountain-valley village.
  "Extreme 4x4": "/images/events/cholistan-jeep-rally.webp",
  "Traditional Sport": "/images/shandur-polo.jpg",
  // Khunjerab, the highest paved border crossing, not an unnamed peak.
  "High Altitude": "/images/licensed/china-border-pakistan.jpg",
};

const TYPE_DESCRIPTIONS: Record<string, string> = {
  "High Trekking": "Multi-day treks across high glacier terrain to the base of the world's greatest peaks - six routes among the finest trekking on Earth.",
  "Glacier Walk": "Accessible glaciers you can walk directly onto from the road - no technical climbing experience required for most.",
  "Extreme White-water": "Grade II-IV commercial rafting through forested gorges and deep river canyons.",
  "High Adrenaline": "Ski resorts, paragliding launch sites, and cross-country flights from some of the country's highest valleys.",
  "Elite Climbing": "World-class big-wall and technical objectives, from the largest vertical cliff face on Earth to unsolved alpine problems.",
  "Extreme 4x4": "Jeep-track expeditions and an annual desert rally covering roughly 500 km of dunes.",
  "Traditional Sport": "Freestyle mountain polo at the world's highest ground, plus tent-pegging and city riding schools.",
  "High Altitude": "Drivable mountain passes and glacial lakes above 3,000 m, several among the highest of their kind on Earth.",
};

export const ADVENTURE_SITES: AdventureSite[] = [
  // High Trekking
  site({
    slug: "k2-base-camp",
    name: "K2 Base Camp",
    type: "High Trekking",
    region: "Gilgit-Baltistan",
    lat: 35.881,
    lng: 76.515,
    blurb: "The trekking objective at the foot of K2's south face, reached via the Baltoro Glacier from Askole - the goal of the K2 Base Camp and Concordia trek.",
    // place-guides.ts: the Baltoro approach is "demanding but non-technical".
    difficulty: "Advanced",
  }),
  site({
    slug: "concordia",
    name: "Concordia",
    type: "High Trekking",
    region: "Gilgit-Baltistan",
    lat: 35.73,
    lng: 76.5,
    blurb: "The 'Throne Room of the Mountain Gods' - the glacial junction where four 8,000 m peaks (K2, Broad Peak, Gasherbrum I & II) are visible at once.",
    // Same Baltoro approach as K2 Base Camp: "demanding but non-technical".
    difficulty: "Advanced",
  }),
  site({
    slug: "gondogoro-la",
    name: "Gondogoro La",
    type: "High Trekking",
    region: "Gilgit-Baltistan",
    lat: 35.7,
    lng: 76.4,
    blurb: "A 5,585 m glacier-pass crossing that closes the loop from Concordia into Hushe Valley, avoiding a backtrack down the Baltoro.",
    // place-guides.ts: "one of the hardest non-technical high-altitude treks
    // on Earth... crampons and fixed ropes... not for inexperienced trekkers".
    difficulty: "Expert",
    elevationM: 5585,
  }),
  site({
    slug: "snow-lake",
    name: "Snow Lake",
    type: "High Trekking",
    region: "Gilgit-Baltistan",
    lat: 36.1,
    lng: 75.4,
    blurb: "The endpoint of a multi-day traverse across the Hispar-Biafo glacier system.",
    // place-guides.ts: "available to experienced trekkers... requires glacier
    // travel skills and crampons; the route is serious".
    difficulty: "Expert",
  }),
  site({
    slug: "baltoro-glacier",
    name: "Baltoro Glacier",
    type: "High Trekking",
    region: "Gilgit-Baltistan",
    lat: 35.72,
    lng: 76.26,
    blurb: "The approach route itself for the K2 Base Camp and Concordia trek.",
    // place-guides.ts: "demanding but non-technical".
    difficulty: "Advanced",
  }),
  site({
    slug: "laila-peak-trek",
    // The trek passes beneath the peak; it does not climb it, so the name
    // says so and no summit height is shown.
    name: "Laila Peak Base Camp",
    type: "High Trekking",
    region: "Gilgit-Baltistan",
    lat: 35.6896,
    lng: 76.4044,
    blurb: "Passed on the Hushe Valley circuit alongside Charakusa.",
    // place-guides.ts: the Hushe circuit is "one of Pakistan's finest
    // non-technical mountain experiences".
    difficulty: "Intermediate",
  }),

  // Glacier Walk
  site({
    slug: "passu-glacier",
    name: "Passu Glacier",
    type: "Glacier Walk",
    region: "Gilgit-Baltistan",
    lat: 36.51,
    lng: 74.89,
    blurb: "A 2-hour walk in from the Karakoram Highway - no crampons required for most of the route.",
    // place-guides.ts: "walk onto the ice without technical equipment...
    // without any real climbing".
    difficulty: "Beginner",
  }),
  site({
    slug: "batura-glacier",
    name: "Batura Glacier",
    type: "Glacier Walk",
    region: "Gilgit-Baltistan",
    lat: 36.5127,
    lng: 74.8688,
    blurb: "Its snout is reachable directly from Passu village.",
    // place-guides.ts: paired with Passu as "a full day of accessible
    // adventure without any real climbing".
    difficulty: "Beginner",
  }),
  site({
    slug: "attabad-lake",
    name: "Attabad Lake",
    // Not a glacier: a lake dammed by the 2010 landslide.
    type: "High Adrenaline",
    region: "Gilgit-Baltistan",
    lat: 36.343,
    lng: 74.862,
    blurb: "Boating and jet-skiing on the lake the Hunza River formed behind the 2010 Attabad landslide.",
  }),
  site({
    slug: "shispare-sar",
    name: "Shispare Sar",
    // A 7,611 m summit is a climbing objective, not a walk.
    type: "Elite Climbing",
    region: "Gilgit-Baltistan",
    lat: 36.44,
    lng: 74.682,
    blurb: "A 7,611 m peak of the Batura Muztagh above Passu and Hunza, climbed only as a full expedition.",
    elevationM: 7611,
  }),

  // Extreme White-water
  site({
    slug: "swat-river-rafting",
    name: "Swat River",
    type: "Extreme White-water",
    region: "Khyber Pakhtunkhwa",
    lat: 35.22,
    lng: 72.44,
    blurb: "Grade II-IV commercial rafting through forested gorges.",
    // place-guides.ts: upper sections run Grade III-IV; the Bahrain-Madyan
    // section is Grade II-III and "more suitable for families" - most
    // commercial operators run the moderate sections.
    difficulty: "Intermediate",
  }),
  site({
    slug: "naran-kaghan-rafting",
    name: "Naran & Kunhar River",
    type: "Extreme White-water",
    region: "Khyber Pakhtunkhwa",
    lat: 34.907,
    lng: 73.649,
    blurb: "Rafting on the Kunhar River through the Kaghan Valley.",
    difficulty: "Beginner",
  }),
  site({
    slug: "skardu-indus-rafting",
    name: "Indus River, Skardu",
    type: "Extreme White-water",
    region: "Gilgit-Baltistan",
    lat: 35.2871,
    lng: 75.6553,
    blurb: "Powerful deep-canyon Indus sections near Skardu.",
    difficulty: "Advanced",
  }),

  // High Adrenaline
  site({
    slug: "malam-jabba",
    name: "Malam Jabba",
    type: "High Adrenaline",
    region: "Khyber Pakhtunkhwa",
    lat: 34.8,
    lng: 72.5667,
    blurb: "Pakistan's largest ski resort - chairlifts, groomed runs, and its own zip line.",
    // GTP intern reference (Skiing_Snowboarding.docx): "an 800-meter ski
    // slope at approximately 2,804 meters elevation".
    elevationM: 2804,
  }),
  site({
    slug: "naltar-ski",
    name: "Naltar Valley",
    type: "High Adrenaline",
    region: "Gilgit-Baltistan",
    lat: 36.16,
    lng: 74.18,
    blurb: "Pakistan Air Force ski runs, with some of the best off-piste snowfields in the country.",
    // Off-piste (ungroomed) snowfields, not beginner terrain.
    difficulty: "Advanced",
    // GTP intern reference (Skiing_Snowboarding.docx): "reaches elevations
    // up to about 2,950 meters, making it the highest ski resort in the
    // country".
    elevationM: 2950,
  }),
  site({
    slug: "khanpur-dam",
    name: "Khanpur Dam",
    type: "High Adrenaline",
    region: "Khyber Pakhtunkhwa",
    lat: 33.807,
    lng: 72.936,
    blurb: "A paragliding launch site near Islamabad.",
  }),
  site({
    slug: "mangla-lake-paragliding",
    name: "Mangla Lake",
    type: "High Adrenaline",
    region: "Punjab",
    lat: 33.117,
    lng: 73.65,
    blurb: "A paragliding launch site at Mangla Lake.",
  }),
  site({
    slug: "patriata-paragliding",
    name: "Patriata",
    type: "High Adrenaline",
    region: "Punjab",
    lat: 33.8743,
    lng: 73.457,
    blurb: "A paragliding launch site near Murree.",
    // GTP intern reference (Paragliding_final.docx): "a dedicated
    // paragliding launch area at Patriata, considered suitable for
    // beginners due to moderate wind conditions".
    difficulty: "Beginner",
  }),
  site({
    slug: "hunza-cross-country-paragliding",
    name: "Hunza Valley",
    type: "High Adrenaline",
    region: "Gilgit-Baltistan",
    lat: 36.333,
    lng: 74.666,
    blurb: "Higher-altitude cross-country paragliding - a nearly 96 km flight has been recorded from around 3,400 m.",
    // The specific activity described here is record-distance XC flying,
    // not tandem tourism - an expert pursuit.
    difficulty: "Expert",
    // GTP intern reference (Paragliding_final.docx): "a notable flight
    // distance of nearly 96 km from a takeoff altitude of roughly 3,409
    // meters" - the precise launch altitude behind the blurb's "~3,400 m".
    elevationM: 3409,
  }),

  // Elite Climbing
  site({
    slug: "trango-towers",
    name: "Trango Towers",
    type: "Elite Climbing",
    region: "Gilgit-Baltistan",
    lat: 35.7166,
    lng: 76.4166,
    blurb: "The world's largest near-vertical cliff face.",
    // place-guides.ts: "one of the hardest multi-day big-wall objectives in
    // the world".
    difficulty: "Expert",
    // Nameless Tower (the main Trango spire) - standard published elevation.
    elevationM: 6239,
  }),
  site({
    slug: "charakusa-valley",
    name: "Charakusa Valley",
    type: "Elite Climbing",
    region: "Gilgit-Baltistan",
    lat: 35.588,
    lng: 76.527,
    blurb: "A concentration of untouched granite spires above Hushe.",
    // place-guides.ts: "rivals the Trango group as the premier granite
    // objective in Pakistan" for technical alpinists.
    difficulty: "Expert",
  }),
  site({
    slug: "gasherbrum-iv",
    name: "Gasherbrum IV",
    type: "Elite Climbing",
    region: "Gilgit-Baltistan",
    lat: 35.758,
    lng: 76.617,
    blurb: "Its Shining Wall is one of mountaineering's last great unsolved problems.",
    // place-guides.ts: "technically one of the most demanding mountains in
    // the world".
    difficulty: "Expert",
    // Real summit elevation, standard published value.
    elevationM: 7925,
  }),
  site({
    slug: "nanga-parbat-rupal-face",
    name: "Nanga Parbat - Rupal Face",
    type: "Elite Climbing",
    region: "Gilgit-Baltistan",
    lat: 35.237,
    lng: 74.589,
    blurb: "At around 4,600 m, the highest mountain wall on the planet.",
    // place-guides.ts: mountaineers regard it as "one of the great
    // challenges in the sport".
    difficulty: "Expert",
    // Mountain's real summit elevation (distinct from the 4,600m wall
    // height quoted in the blurb above) - standard published value.
    elevationM: 8126,
  }),

  // Extreme 4x4
  site({
    slug: "askole",
    name: "Askole",
    type: "Extreme 4x4",
    region: "Gilgit-Baltistan",
    lat: 35.679,
    lng: 75.819,
    blurb: "The last village before the Baltoro Glacier - jeep-track terminus and expedition staging point.",
  }),
  site({
    slug: "shimshal-valley",
    name: "Shimshal Valley",
    type: "Extreme 4x4",
    region: "Gilgit-Baltistan",
    lat: 36.43,
    lng: 75.32,
    blurb: "One of the Karakoram Highway's classic jeep-extension drives.",
  }),
  site({
    slug: "derawar-fort-cholistan",
    name: "Derawar Fort & Cholistan Desert",
    type: "Extreme 4x4",
    region: "Punjab",
    lat: 28.768,
    lng: 71.333,
    blurb: "400 km of sand dunes for 4x4 and motorbike touring; the annual Cholistan Desert Jeep Rally (running since 2005) covers a roughly 500 km course past the fort each February.",
  }),
  site({
    slug: "chapursan-valley",
    name: "Chapursan Valley",
    type: "Extreme 4x4",
    region: "Gilgit-Baltistan",
    lat: 36.733,
    lng: 74.733,
    blurb: "One of the Karakoram Highway's classic jeep-extension drives, toward the Afghan border.",
  }),
  site({
    slug: "hingol-national-park",
    name: "Hingol National Park",
    type: "Extreme 4x4",
    region: "Balochistan",
    lat: 25.51,
    lng: 65.49,
    blurb: "An off-road route through lunar badlands, sea cliffs and the Makran coast; home to the annual Hingol Off-Road Rally, Pakistan's signature desert motorsport event.",
  }),

  // Traditional Sport
  site({
    slug: "shandur-polo-festival",
    name: "Shandur Pass",
    type: "Traditional Sport",
    region: "Khyber Pakhtunkhwa / Gilgit-Baltistan",
    lat: 36.061,
    lng: 72.516,
    blurb: "The world's highest polo ground at 3,700 m - Chitral vs Gilgit in freestyle mountain polo each July, with no written rulebook.",
    elevationM: 3700,
  }),
  site({
    slug: "chitral-polo",
    name: "Chitral",
    type: "Traditional Sport",
    region: "Khyber Pakhtunkhwa",
    lat: 35.851,
    lng: 71.786,
    blurb: "One side of the historic Shandur Polo Festival rivalry against Gilgit.",
  }),
  site({
    slug: "gilgit-polo",
    name: "Gilgit",
    type: "Traditional Sport",
    region: "Gilgit-Baltistan",
    lat: 35.9208,
    lng: 74.3083,
    blurb: "The other side of the Shandur Polo Festival rivalry against Chitral.",
  }),
  site({
    slug: "bahawalpur-tent-pegging",
    name: "Bahawalpur",
    type: "Traditional Sport",
    region: "Punjab",
    lat: 29.3956,
    lng: 71.6836,
    blurb: "Home to Asia Riding Club's dedicated tent-pegging (Neza Bazi) program.",
  }),

  // High Altitude
  site({
    slug: "karambar-lake",
    name: "Karambar Lake",
    type: "High Altitude",
    region: "Gilgit-Baltistan",
    lat: 36.993,
    lng: 73.617,
    blurb: "One of the world's highest lakes, at 4,275 m.",
    elevationM: 4275,
  }),
  site({
    slug: "lake-saiful-muluk",
    name: "Lake Saiful Muluk",
    type: "High Altitude",
    region: "Khyber Pakhtunkhwa",
    lat: 34.8767,
    lng: 73.6936,
    blurb: "At 3,224 m, surrounded by glaciated peaks.",
    elevationM: 3224,
  }),
  site({
    slug: "ratti-gali-lake",
    name: "Ratti Gali Lake",
    type: "High Altitude",
    region: "Azad Jammu and Kashmir",
    lat: 34.7975,
    lng: 74.0875,
    blurb: "At 3,680 m, in Azad Kashmir.",
    elevationM: 3680,
  }),
  site({
    slug: "deosai-sheosar",
    name: "Deosai",
    type: "High Altitude",
    region: "Gilgit-Baltistan",
    lat: 35.0,
    lng: 75.4,
    blurb: "Home to Sheosar Lake at 4,114 m, on the 'Land of Giants' plateau.",
    elevationM: 4114,
  }),
  site({
    slug: "babusar-top",
    name: "Babusar Top",
    type: "High Altitude",
    region: "Khyber Pakhtunkhwa",
    lat: 35.1167,
    lng: 74.0333,
    blurb: "A drivable pass at 4,173 m with dramatic mountain views.",
    elevationM: 4173,
  }),
  site({
    slug: "khunjerab-pass",
    name: "Khunjerab Pass",
    type: "High Altitude",
    region: "Gilgit-Baltistan",
    lat: 36.851,
    lng: 75.421,
    blurb: "A drivable pass at 4,693 m - the highest paved border crossing on Earth.",
    elevationM: 4693,
  }),
];

export const ADVENTURE_TYPES: { type: string; color: string }[] = Array.from(
  new Map(ADVENTURE_SITES.map((s) => [s.type, s.color])),
).map(([type, color]) => ({ type, color }));

/** One card per activity type, in the same order highlights already appear in on this page. */
export const ADVENTURE_TYPE_META: AdventureTypeMeta[] = ADVENTURE_TYPES.map(({ type, color }) => ({
  type,
  color,
  image: TYPE_IMAGES[type],
  description: TYPE_DESCRIPTIONS[type],
  count: ADVENTURE_SITES.filter((s) => s.type === type).length,
}));
